// frontend/src/components/engineer/EngineerDashboard.tsx - FINAL VERSION WITH CHANGE PASSWORD
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { AuthUser } from '../../services/auth.service';
import EngineerHeader from './EngineerHeader';
import EngineerSidebar from './EngineerSidebar';
import EngineerMainContent from './EngineerMainContent';
import ChangePassword from '../auth/ChangePassword';
import styles from './EngineerDashboard.module.css';
// ADD THESE NEW IMPORTS
import ChangeLogModal from './modals/ChangeLogModal';
import { changeLogService, ChangeLog } from '../../services/changeLog.service';

interface EngineerDashboardProps {
  user: AuthUser;
  onLogout: () => void;
}

const EngineerDashboard: React.FC<EngineerDashboardProps> = ({ user, onLogout }) => {
  const [activePage, setActivePage] = useState('dashboard');
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  
  // UI State
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const [showChangeLog, setShowChangeLog] = useState(false);
  const [activeChangeLog, setActiveChangeLog] = useState<ChangeLog | null>(null);

  console.log('🏗️ Engineer Dashboard rendering for:', user.name);

  // Main navigation handler
  const handlePageChange = useCallback((pageId: string, params?: string) => {
    if (isTransitioning) {
      console.log('🚫 Navigation blocked: transition in progress');
      return;
    }

    console.log('🧭 Engineer Dashboard: Navigating to page:', pageId, params);
    
    setIsTransitioning(true);
    
    // Handle different parameter types
    let newProjectId: string | undefined;
    
    if (typeof params === 'string') {
      newProjectId = params;
    }
    
    // Prevent unnecessary updates
    if (pageId === activePage && newProjectId === projectId) {
      console.log('🔄 Same page and parameters, skipping update');
      setIsTransitioning(false);
      return;
    }
    
    setActivePage(pageId);
    setProjectId(newProjectId);
    
    // Reset transitioning state
    setTimeout(() => {
      setIsTransitioning(false);
    }, 150);
  }, [activePage, projectId, isTransitioning]);

  // Handle sidebar navigation (clears parameters)
  const handleSidebarPageChange = useCallback((pageId: string) => {
    console.log('📂 Engineer Sidebar navigation to:', pageId);
    handlePageChange(pageId, undefined);
  }, [handlePageChange]);

  // Handle sidebar toggle
  const handleSidebarToggle = useCallback((collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
    console.log('🔄 Engineer Sidebar toggled:', collapsed ? 'collapsed' : 'expanded');
  }, []);

  // Handle Change Password
  const handleChangePassword = useCallback(() => {
    console.log('📝 Engineer Dashboard: Opening Change Password modal');
    setShowChangePassword(true);
  }, []);

  // Handle Close Change Password
  const handleCloseChangePassword = useCallback(() => {
    console.log('📝 Engineer Dashboard: Closing Change Password modal');
    setShowChangePassword(false);
  }, []);

  useEffect(() => {
    checkForChangeLog();
  }, []);

  const checkForChangeLog = async () => {
    const userId = user?.employeeNo || user?.employeeNumber || user?.uid;
    if (!userId) return;

    try {
      const result = await changeLogService.shouldShowChangeLog(userId);
      
      if (result.shouldShow && result.changeLog) {
        setActiveChangeLog(result.changeLog);
        setShowChangeLog(true);
        console.log('📋 Showing change log to user:', userId);
      }
    } catch (error) {
      console.error('Error checking change log:', error);
    }
  };

  const handleChangeLogAcknowledge = () => {
    setShowChangeLog(false);
    setActiveChangeLog(null);
    console.log('✅ User acknowledged change log');
  };

  // Memoized props for performance
  const sidebarProps = useMemo(() => ({
    activePage,
    onPageChange: handleSidebarPageChange,
    isTransitioning,
    onToggle: handleSidebarToggle,
    user
  }), [activePage, handleSidebarPageChange, isTransitioning, handleSidebarToggle, user]);

  const mainContentProps = useMemo(() => ({
    activePage,
    projectId,
    onNavigate: handlePageChange,
    isTransitioning,
    isSidebarCollapsed,
    user
  }), [activePage, projectId, handlePageChange, isTransitioning, isSidebarCollapsed, user]);

  const headerProps = useMemo(() => ({
    user,
    onLogout,
    onNavigate: handlePageChange,
    onChangePassword: handleChangePassword
  }), [user, onLogout, handlePageChange, handleChangePassword]);

  // Debug logging
  React.useEffect(() => {
    console.log('🏗️ Engineer Dashboard State Update:', {
      activePage,
      projectId,
      isSidebarCollapsed,
      isTransitioning,
      showChangePassword,
      user: {
        name: user?.name,
        userType: user?.userType,
        isEngineer: user?.isEngineer,
        department: user?.department,
        permissions: user?.permissions
      }
    });
  }, [activePage, projectId, isSidebarCollapsed, isTransitioning, showChangePassword, user]);

  return (
    <div className={styles.engineerDashboardContainer}>
      {/* Engineer Header */}
      <EngineerHeader {...headerProps} />
      
      {/* Main Dashboard Body */}
      <div className={styles.engineerDashboardBody}>
        {/* Sidebar */}
        <EngineerSidebar {...sidebarProps} />
        
        {/* Main Content Area */}
        <EngineerMainContent {...mainContentProps} />
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePassword 
          user={user} 
          onClose={handleCloseChangePassword} 
        />
      )}

      {/* 🆕 NEW: Change Log Modal */}
      {showChangeLog && activeChangeLog && (
        <ChangeLogModal
          changeLog={activeChangeLog}
          user={user}
          onAcknowledge={handleChangeLogAcknowledge}
        />
      )}
    </div>
  );
};

export default React.memo(EngineerDashboard);